import type { AuthClient } from "@nav-games/auth-client";
import { setPremium } from "@nav-games/games-core";
import { Iap, type Product, type PurchaseResult } from "@nav-games/iap";

import { WORKER_BASE_URL } from "./config";
import { refreshEntitlements } from "./entitlements";

/// Single non-consumable IAP product. £0.99 unlocks every premium
/// tier across modules (Morse flashcards intermediate/expert,
/// future modules' equivalents). Configure this exact ID in both
/// App Store Connect and Play Console.
export const PREMIUM_PRODUCT_ID = "com.goastern.navgames.premium";

/// Fetch the local price string for the Premium product. Returns
/// `null` on native platforms when the store is unreachable (e.g.
/// no Play Services on the device) and on the web, where IAP is
/// disabled — the SPA falls back to a hardcoded "£0.99" copy.
export async function fetchPremiumProduct(): Promise<Product | null> {
  try {
    const result = await Iap.getProducts({ ids: [PREMIUM_PRODUCT_ID] });
    return result.products[0] ?? null;
  } catch {
    return null;
  }
}

/// Trigger the platform's purchase UI, then forward the resulting
/// receipt to `nav-games-worker` for verification. Resolves with
/// the new premium state; rejects with an error string the UI can
/// display.
export async function purchasePremium(auth: AuthClient): Promise<void> {
  const session = auth.session();
  if (!session) throw new Error("not signed in");

  const receipt = await Iap.purchase({ id: PREMIUM_PRODUCT_ID });
  await verifyReceiptWithWorker(receipt, session.accessToken);

  // Update the cached premium flag immediately. The next
  // `refreshEntitlements` (e.g. on app re-open) reconciles against
  // the worker's authoritative state.
  setPremium(true);
  // Best-effort re-sync — gives us `expiresAt` on subscriptions.
  void refreshEntitlements(auth);
}

/// Re-acquire prior purchases. Required by App Store guidelines.
export async function restorePurchases(auth: AuthClient): Promise<void> {
  const session = auth.session();
  if (!session) throw new Error("not signed in");
  const result = await Iap.restorePurchases();
  for (const txn of result.transactions) {
    try {
      await verifyReceiptWithWorker(txn, session.accessToken);
    } catch {
      /* skip and try the next — restore is best-effort */
    }
  }
  void refreshEntitlements(auth);
}

async function verifyReceiptWithWorker(
  receipt: PurchaseResult,
  accessToken: string,
): Promise<void> {
  const path =
    receipt.platform === "apple" ? "/iap/apple/verify" : "/iap/google/verify";
  const body: Record<string, unknown> =
    receipt.platform === "apple"
      ? { signedTransactionInfo: receipt.signedTransactionInfo }
      : {
          purchaseToken: receipt.purchaseToken,
          productId: receipt.productId,
          packageName: receipt.packageName,
          productType: receipt.productType,
        };
  const resp = await fetch(`${WORKER_BASE_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`worker rejected receipt (${resp.status}): ${text}`);
  }
}
